/**
 * Gang model shared by client and server (pure data, no DOM/Node): permissions, default role
 * templates, cosmetics, progression tiers and chat/crew limits.
 *
 * Roles are DATA (rows in `gang_roles`: name, rank, permission list, daily withdraw limit). The names
 * below are only the templates a new gang starts with — leaders can rename, add and delete roles.
 * Progression unlocks OPTIONS (members, stash room, lockup tier, cosmetics), never combat power.
 */

/**
 * Every permission a role can hold. Order is the display order.
 * Property permissions (gang businesses, docs/GANGS.md §8, docs/PROPERTIES.md §15):
 *  - `property.manage`  order supplies and pay the running costs of gang premises (from the treasury); run a
 *                       mate's OWN premises when that mate opted in (P1)
 *  - `property.buy`     buy / take premises for the gang and fit an operation (treasury); accept premises
 *                       moved into or bought out of the gang
 *  - `property.collect` box up finished stock, sell the takings to the gang's wholesaler (income → treasury),
 *                       move stock in and out of a gang premises' store
 *  - `property.upgrade` buy improvements for gang premises (treasury)
 * Hideout permission (WP-X4, docs/HIDEOUT.md):
 *  - `hideout.furnish`  buy furnishings and the livery for the gang's hideout (treasury; status only)
 */
export const GANG_PERMISSIONS = [
  'invite',
  'kick',
  'promote',
  'treasury.deposit',
  'treasury.withdraw',
  'property.manage',
  'property.buy',
  'property.collect',
  'property.upgrade',
  'hideout.furnish',
  'jobs.start',
  'inventory.manage',
  'roles.edit',
  'audit.view',
] as const;
export type GangPermission = (typeof GANG_PERMISSIONS)[number];
export const isGangPermission = (p: string): p is GangPermission => (GANG_PERMISSIONS as readonly string[]).includes(p);

export interface RoleTemplate {
  name: string;
  /** Higher = more senior. Boss is always the top rank. */
  rank: number;
  permissions: readonly GangPermission[];
  /** Pounds a holder may withdraw from the treasury per rolling 24 h. null = unlimited. */
  withdrawLimit: number | null;
  /** The boss role: holds every permission implicitly, can't be deleted, at least one holder always exists. */
  boss?: boolean;
}

export const DEFAULT_ROLES: readonly RoleTemplate[] = [
  { name: 'Boss', rank: 100, permissions: GANG_PERMISSIONS, withdrawLimit: null, boss: true },
  {
    name: 'Lieutenant',
    rank: 80,
    permissions: ['invite', 'kick', 'promote', 'treasury.deposit', 'treasury.withdraw', 'property.manage', 'property.collect', 'property.upgrade', 'hideout.furnish', 'jobs.start', 'inventory.manage', 'audit.view'],
    withdrawLimit: 5_000,
  },
  { name: 'Enforcer', rank: 60, permissions: ['invite', 'kick', 'treasury.deposit', 'jobs.start', 'inventory.manage'], withdrawLimit: 0 },
  { name: 'Member', rank: 40, permissions: ['treasury.deposit', 'jobs.start'], withdrawLimit: 0 },
  { name: 'Prospect', rank: 20, permissions: ['treasury.deposit'], withdrawLimit: 0 },
];
/** Where new members start unless the inviter picks another (lower) role. */
export const DEFAULT_JOIN_ROLE = 'Prospect';

export const MAX_ROLES = 12;
export const ROLE_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 '\-]{0,19}$/;
export const ROLE_RANK_MIN = 1;
/** Custom roles must rank below the boss role (rank 100). */
export const ROLE_RANK_MAX = 99;

// ----------------------------------------------------------------------------- creation & cosmetics

/** Founding a gang is a money sink (it goes to the `gang-founding` sink wallet). */
export const GANG_CREATE_COST = 3_000;
export const GANG_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 '\-]{2,23}$/;
export const GANG_TAG_RE = /^[A-Za-z0-9]{2,4}$/;
/** Words that can't be (part of) a gang name/tag: impersonation of staff/system. */
export const GANG_RESERVED_WORDS = ['admin', 'system', 'moderator', 'police', 'staff', 'gm'] as const;

export const GANG_EMBLEMS = ['skull', 'crown', 'anchor', 'dagger', 'rose', 'star', 'crow', 'wheel', 'key', 'wave', 'eye', 'bolt'] as const;
export type GangEmblem = (typeof GANG_EMBLEMS)[number];
/** Emblems available from the start; the rest unlock with gang tier. */
const BASE_EMBLEMS = 6;

/** Colour presets (0xRRGGBB). The first block is free; later blocks unlock with gang tier. */
export const GANG_COLORS: readonly number[] = [
  0xc0392b, 0x2980b9, 0x27ae60, 0xf39c12, 0x8e44ad, 0x16a085, 0xecf0f1, 0x2c3e50, // tier 0
  0xd35400, 0x1abc9c, 0xe84393, 0x6c5ce7, // tier 1
  0xb8860b, 0x708090, 0x800020, 0x004953, // tier 2
  0xffd700, 0xc0c0c0, 0x39ff14, 0xff1493, // tier 3
];

// ----------------------------------------------------------------------------- progression

export interface GangTier {
  tier: number;
  /** Reputation needed. */
  rep: number;
  maxMembers: number;
  /** Storage units in the shared stash. */
  stashUnits: number;
  /** Highest lockup tier a gang may take (consumed by property systems). */
  lockupTier: number;
  /** How many cosmetic colours/emblems are open. */
  colors: number;
  emblems: number;
}

export const GANG_TIERS: readonly GangTier[] = [
  { tier: 0, rep: 0, maxMembers: 6, stashUnits: 12, lockupTier: 1, colors: 8, emblems: BASE_EMBLEMS },
  { tier: 1, rep: 150, maxMembers: 10, stashUnits: 24, lockupTier: 1, colors: 12, emblems: 8 },
  { tier: 2, rep: 600, maxMembers: 14, stashUnits: 48, lockupTier: 2, colors: 16, emblems: 10 },
  { tier: 3, rep: 1_800, maxMembers: 20, stashUnits: 96, lockupTier: 3, colors: 20, emblems: 12 },
];

export function gangTier(reputation: number): GangTier {
  let t = GANG_TIERS[0]!;
  for (const g of GANG_TIERS) if (reputation >= g.rep) t = g;
  return t;
}

export const isColorUnlocked = (color: number, tier: GangTier): boolean => {
  const i = GANG_COLORS.indexOf(color);
  return i >= 0 && i < tier.colors;
};
export const isEmblemUnlocked = (emblem: string, tier: GangTier): boolean => {
  const i = (GANG_EMBLEMS as readonly string[]).indexOf(emblem);
  return i >= 0 && i < tier.emblems;
};

/**
 * Reputation can only come from coordinated activity, and is capped per day so it can't be ground out
 * by one repeatable action (the activity module decides amounts; this is the backstop).
 */
export const GANG_REP_DAILY_CAP = 200;

/**
 * Where reputation comes from (every source goes through `recordProgress`, idempotent per key, under the
 * daily cap). Until 2026-10 only Night Freight paid reputation, so a gang that ran a business or fought for
 * turf never moved up a tier; the gang's other shared work counts now too (docs/GANGS.md §3).
 */
export const GANG_REP = {
  /** A Night Freight run with two or more members of the gang (or paid from the treasury): base + per crate delivered. */
  freightBase: 10,
  freightPerCrate: 3,
  /** A firm's job (WP-X3: the Savings Bank) pulled off with two or more members of the gang on it. */
  crewJob: 20,
  /** The gang came to run a district (at most once per district per day). */
  turfLead: 10,
  /** The gang won a contest for a district — took it, or held it against a challenger. */
  turfContest: 30,
  /** Takings sold to the gang's wholesaler: 1 per this many pounds, at most `takingsMax` a sale. */
  takingsPerPounds: 250,
  takingsMax: 15,
} as const;

/** Reputation for one wholesale sale of `total` pounds (0 below the first step). */
export const takingsReputation = (total: number): number => Math.min(GANG_REP.takingsMax, Math.floor(Math.max(0, total) / GANG_REP.takingsPerPounds));

// ----------------------------------------------------------------------------- invites & treasury

export const INVITE_TTL_MS = 10 * 60_000;
/** Pending invites a gang may have out at once (anti-spam), and one character may hold. */
export const MAX_PENDING_INVITES_PER_GANG = 20;
export const MAX_PENDING_INVITES_PER_CHARACTER = 10;
export const WITHDRAW_WINDOW_MS = 24 * 3_600_000;
/** Fresh members can't draw from the treasury until they have been in the gang this long. */
export const WITHDRAW_MIN_TENURE_MS = 60 * 60_000;

// ----------------------------------------------------------------------------- chat, crews, emotes

export const CHAT_MAX_LEN = 200;
/** Proximity ("say") chat reaches this far, in metres. */
export const CHAT_SAY_RADIUS = 45;
export const CHAT_RATE = { perSecond: 0.5, burst: 4 } as const;
/** Same text twice within this window is dropped. */
export const CHAT_DUPLICATE_MS = 8_000;
export const CHAT_HISTORY = 30;

/** A crew seats five (was 4 until 2026-10: a group of five friends had to split up). */
export const CREW_MAX = 5;
export const CREW_INVITE_TTL_MS = 60_000;
/** An offline crew member keeps their seat (across reconnects and server restarts) this long. */
export const CREW_OFFLINE_TTL_MS = 12 * 3_600_000;

/** Emote ids travel in the player flags of snapshots (bits 8..15). 0 = none. */
export const EMOTES = ['wave', 'point', 'cheer', 'shrug', 'salute', 'facepalm', 'beckon', 'sit'] as const;
export type EmoteName = (typeof EMOTES)[number];
export const EMOTE_DURATION_MS = 4_000;
export const emoteId = (name: EmoteName): number => EMOTES.indexOf(name) + 1;
export const emoteName = (id: number): EmoteName | null => EMOTES[id - 1] ?? null;
